/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MicDrop } from './MicDrop';
import { StackDetails } from './StackDetails';
import { Link, useSupportConfig } from '@backstage/core-components';
import { Button } from '@material-ui/core';

interface IErrorPageProps {
  status?: string;
  statusMessage: string;
  additionalInfo?: React.ReactNode;
  supportUrl?: string;
  stack?: string;
}

/** @public */
export type ErrorPageClassKey = 'container' | 'title' | 'subtitle';

const useStyles = makeStyles(
  theme => ({
    container: {
      padding: theme.spacing(8),
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(2),
      },
    },
    title: {
      [theme.breakpoints.down('xs')]: {
        paddingBottom: theme.spacing(4),
        fontSize: theme.typography.h3.fontSize,
      },
    },
    mainTitle: {
      paddingBottom: theme.spacing(1),
      [theme.breakpoints.down('xs')]: {
        paddingBottom: theme.spacing(4),
        fontSize: theme.typography.h3.fontSize,
      },
    },
    subtitle: {
      paddingBottom: theme.spacing(2),
      color: theme.palette.textSubtle,
    },
    buttons: {
      marginBottom: theme.spacing(4),
    },
  }),
  { name: 'BackstageErrorPage' },
);

const mailToSupport = () => {
  const subject = encodeURIComponent('Support Request: Backstage TechDocs');
  const timestamp = new Date().toLocaleString();
  const url = window.location.href;
  const bodyText = `Hello,\n\nI need help with the following URL: ${url}\n\nTime: ${timestamp}`;
  const body = encodeURIComponent(bodyText);
  const mailto = `mailto:aaltamirano@newscorp.com,Gabriel.Matusevich@dowjones.com?subject=${subject}&body=${body}`;

  return mailto;
};

/**
 * Error page with status and description
 *
 * @public
 *
 */
export function ErrorPage(props: IErrorPageProps) {
  const {
    status = '',
    statusMessage,
    additionalInfo,
    supportUrl,
    stack,
  } = props;
  const classes = useStyles();
  const navigate = useNavigate();
  const support = useSupportConfig();

  const finalMessage =
    statusMessage === 'PAGE NOT FOUND'
      ? 'SOMEONE MOVED THE COFFEE POT'
      : statusMessage;

  return (
    <Grid container className={classes.container}>
      <Grid item xs={12} sm={8} md={4}>
        <Typography
          data-testid="error"
          variant="body1"
          className={classes.subtitle}
        >
          {`ERROR ${status}: ${finalMessage}`}
        </Typography>

        <Typography variant="h2" className={classes.mainTitle}>
          Oops! Looks like this feature went out for a coffee break.
        </Typography>
        <Typography variant="body1" className={classes.subtitle}>
          {additionalInfo}
        </Typography>
        <Typography variant="h6" className={classes.title}>
          Please try again later,{' '}
          <Link to={supportUrl || support.url}>report an issue</Link> or reach
          out to our support team if you need immediate assistance.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          className={classes.buttons}
          href={mailToSupport()}
        >
          Reach out to our support team
        </Button>
        <Typography variant="h6" className={classes.title}>
          Or you can{' '}
          <Link to="#" data-testid="go-back-link" onClick={() => navigate(-1)}>
            go back
          </Link>{' '}
          to the previous page.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(-1)}
        >
          Go Back
        </Button>
        {stack && <StackDetails stack={stack} />}
      </Grid>
      <MicDrop />
    </Grid>
  );
}
